import React, { useMemo } from "react";
import { Form, type FormControlProps } from "banco-components";
import { FormControllerWrapper, type FormControllerWrapperProps } from "./FormControllerWrapper";
import { get } from "lodash";
import {
  Controller, type ControllerRenderProps,
  useFormContext } from "react-hook-form";
import { handleDefaultDataProps } from "./utils/inputDataPropsHandler";
import { formatOptionsList, updateFieldWithValidation } from "./utils/utils";

interface SelectInputProps extends FormControllerWrapperProps {
    value?: string
    isDisabled?: boolean
    onChangeHandler?: (
        e: React.ChangeEvent<HTMLSelectElement>,
        field: ControllerRenderProps
      ) => void;
    afterOnChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
    inputProps?: React.InputHTMLAttributes<HTMLSelectElement> & FormControlProps & {
        [key: `data-${string}`]: string
    }
    optionsList?: Record<string, string | number> |
      { name: string, label?: string | JSX.Element, value: string | number }[]
      | string[]
      | number[];
    optionsListSort?: boolean // Should the options list be sorted
    defaultLabel?: string
    autoComplete?: string // Sets the autoComplete attribute on the input
}

/**
 * Used within a form for capturing a select input
 *
 * @example
 * <SelectInput
 *  fieldName="length_at_address"
 *  defaultLabel="Select an option"
 *  autoComplete="off"
 *  optionsList={["0-6 months", "6-12 months", "1-2 years", "2-3 years", "3-4 years", "4+ years"]}
 * />
 *
 * @example
 * <SelectInput
 *  fieldName="length_at_address"
 *  defaultLabel="Please select an option"
 *  optionsList={[ {value: "0-6", label: "0-6 Months"}, {value: "6-12", label: "6-12 Months"} ]}
 * />
 *
 * @param value - The selected value from the options list
 * @param isDisabled - Whether the radio button is disabled
 * @param onChangeHandler - Custom handler for checkbox change (does not update form value)
 * @param afterOnChange - Custom handler to fire after the form value changes
 * @param inputProps - Additional props to pass to the input
 * @param controllerProps - Additional props to pass to the controller
 * @param optionsList - The list of options to display
 * @param defaultLabel - Adds a default label to the select input with an empty string value
 * @param autoComplete - Sets the autoComplete attribute on the input
 */
export const SelectInput = ({
  value, isDisabled, onChangeHandler, afterOnChange, inputProps, defaultLabel,
  optionsList, optionsListSort, controllerProps, autoComplete,
  ...rest }: SelectInputProps) => {
  const formMethods = useFormContext();

  const err = get(formMethods.formState.errors, rest.fieldName);
  const mergedInputProps = handleDefaultDataProps({ ...inputProps }, rest.fieldName, true);

  const formattedOptionsList = useMemo(() => {
    const options = [];

    if (defaultLabel !== undefined) {
      options.push({ value: "", disabled: true, label: defaultLabel });
    }
    formatOptionsList(optionsList, optionsListSort === true).forEach((option) => {
      options.push({ value: option.value, disabled: false, label: option.label || option.name });
    });

    return options;
  }, [defaultLabel, optionsList, optionsListSort]);

  return (
    <FormControllerWrapper { ...rest } isDisabled={ isDisabled } showFeedback>
      <Controller
        name={ rest.fieldName }
        control={ formMethods.control }
        defaultValue={ formattedOptionsList[0]?.value || "" }
        { ...controllerProps }
        render={ ({ field }) => (
          <Form.Control
            id={ rest.fieldName }
            autoComplete={ autoComplete }
            as="select"
            isInvalid={ Boolean(err) }
            disabled={ isDisabled }
            { ...field }
            { ...mergedInputProps }
            onChange={ (e: React.ChangeEvent<HTMLSelectElement>) => {
              if (onChangeHandler) {
                onChangeHandler(e, field);
              } else {
                updateFieldWithValidation({
                  field, value: e, trigger: formMethods.trigger,
                  fieldName: rest.fieldName, afterOnChange
                });
              }
            } }>
            { formattedOptionsList.map((option) => {
              return (
                <option key={ option.value } disabled={ option.disabled } value={ option.value }>
                  { option.label }
                </option>
              );
            }) }
          </Form.Control>
        ) }
      />
    </FormControllerWrapper>
  );
};
